import {api} from './api';

export interface SimulacaoCreditoRequest {
  valor_centavos: number;
  prazo_meses: number;
  mova_product_id: number;
  anniversary_day?: number;
}

export interface InstallmentOptionComplementInfo {
  tax_iof_fixed_rate: number;
  tax_iof_daily_rate: number;
  tax_iof_total_rate: number;
  tax_iof_total_value: number;
  costs_fixed_share: number;
  costs_rate_share: number | null;
  interest_total_value: number;
  gross_costs_total_value: number;
  costs_percentage_share: number | null;
  other_services_total_value: number;
}

export interface InstallmentOption {
  installments_option_id: string;
  operation_net_amount: number;
  installments_number: number;
  installment_amount: number;
  installments_sum: number;
  interest_monthly_rate: number;
  interest_annual_rate: number;
  gross_costs_monthly_rate: number;
  gross_costs_annual_rate: number;
  operation_total_taxes: number;
  operation_total_costs: number;
  operation_gross_amount: number;
  first_installment_date: string;
  last_installment_date: string;
  installments_option_complement_info: InstallmentOptionComplementInfo;
}

export interface SimulatorConditions {
  max_operation_net_amount: number;
  min_installments_number: number;
  max_installments_number: number;
  anniversary_day: number[];
}

export interface SimulacaoData {
  general_info: {
    response_type: string;
    operation_tracking_id: string;
    external_id: string;
    quotation_id: number;
  };
  financial_info: {
    borrower_credit_limit: number | null;
    score_number: number;
    rating: string;
  };
  installments_options: InstallmentOption[];
  simulator_conditions: SimulatorConditions;
  additional_fields: any;
  payments_options: any[];
}

export interface SimulacaoCreditoResponse {
  operacao_id: string;
  status: string;
  valor_solicitado_centavos: number;
  prazo_meses: number;
  mova: {
    operation_tracking_id: string;
    quotation_id: number;
    simulacao: {
      api_version: string;
      transaction_id: string;
      data: SimulacaoData;
    };
  };
}

export async function simularCredito(
  payload: SimulacaoCreditoRequest,
): Promise<SimulacaoCreditoResponse> {
  const {data} = await api.post<SimulacaoCreditoResponse>(
    '/credito/mova/simulacoes',
    payload,
  );
  return data;
}

export interface SalvarEnderecoRequest {
  cep: string;
  logradouro: string;
  numero: string;
  complemento?: string;
  bairro: string;
  cidade: string;
  uf: string;
}

export interface SalvarEnderecoResponse {
  id: string;
  active: boolean;
}

export async function salvarEnderecoCredito(
  payload: SalvarEnderecoRequest,
): Promise<SalvarEnderecoResponse> {
  const {data} = await api.post<SalvarEnderecoResponse>(
    '/credito/mova/endereco',
    payload,
  );
  return data;
}

// --- Domicílio (dados bancários / PIX) ---

export interface SalvarDomicilioRequest {
  tipo_chave_pix: 'cpf' | 'cnpj' | 'email' | 'telefone' | 'chave_aleatoria';
  chave_pix: string;
  codigo_banco: string;
  agencia: string;
  digito_agencia?: string;
  conta: string;
  digito_conta: string;
  tipo_conta: 'corrente' | 'poupanca';
  tipo_transferencia: string;
}

export interface SalvarDomicilioResponse {
  id: string;
  active: boolean;
  transfer_type: string;
}

export async function salvarDomicilioCredito(
  payload: SalvarDomicilioRequest,
): Promise<SalvarDomicilioResponse> {
  const {data} = await api.post<SalvarDomicilioResponse>(
    '/credito/mova/domicilio',
    payload,
  );
  return data;
}

// --- Documentos (selfie + RG/CNH) ---

export type TipoDocumento = 'rg_cnh_frente' | 'rg_cnh_verso' | 'selfie';

export interface EnviarDocumentoResponse {
  id: string;
  operacao_id: string;
  doc_type: string;
}

export async function enviarDocumentoCredito(
  operacao_id: string,
  tipo: TipoDocumento,
  fileUri: string,
  doc_subtype?: string,
): Promise<EnviarDocumentoResponse> {
  const formData = new FormData();
  formData.append('operacao_id', operacao_id);
  formData.append('tipo', tipo);
  if (doc_subtype) {
    formData.append('doc_subtype', doc_subtype);
  }

  const uri = fileUri.startsWith('file://') ? fileUri : `file://${fileUri}`;
  const fileName = fileUri.split('/').pop() || `${tipo}.jpg`;
  formData.append('file', {
    uri,
    type: 'image/jpeg',
    name: fileName,
  } as any);

  const {data} = await api.post<EnviarDocumentoResponse>(
    '/credito/mova/documentos',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return data;
}

// --- Proposta ---

export interface EnviarPropostaRequest {
  operacao_id: string;
  installments_option_id: string;
  sync: boolean;
}

export interface EnviarPropostaResponse {
  operacao_id: string;
  status: string;
  operation_tracking_id: string;
  quotation_id: number;
  proposal_id: number;
  proposal_status: string;
  disbursement_target_date: string;
  cpf_mask: string;
  operation_net_amount: number;
  installments_number: number;
  installment_amount: number;
  installments_sum: number;
  first_installment_date: string;
  last_installment_date: string;
  interest_monthly_rate: number;
  interest_annual_rate: number;
  operation_total_taxes: number;
  operation_gross_amount: number;
  iof_total_value: number;
  document_description: string;
  signature_description: string;
  terms_content: string;
  can_request_token: boolean;
  can_sign: boolean;
  can_get_contract_link: boolean;
}

export async function enviarPropostaCredito(
  payload: EnviarPropostaRequest,
): Promise<EnviarPropostaResponse> {
  const {data} = await api.post<EnviarPropostaResponse>(
    '/credito/mova/propostas',
    payload,
  );
  return data;
}

// --- Assinatura (MOVA 2-step flow) ---

// Resumo da assinatura
export interface AssinaturaResumoFinancial {
  operation_net_amount: number;
  installments_number: number;
  installment_amount: number;
  first_installment_date: string;
  last_installment_date: string;
  interest_monthly_rate: number;
  interest_annual_rate?: number;
  operation_gross_amount?: number;
  iof_total_value?: number;
  gross_costs_annual_rate?: number;
}

export interface AssinaturaResumoSignature {
  document_description: string;
  signature_description: string;
  terms_content: string;
}

export interface AssinaturaResumoNextActions {
  can_request_token: boolean;
  can_sign: boolean;
  can_get_contract_link: boolean;
  recommended: string;
}

export interface AssinaturaResumoMova {
  operation_tracking_id: string;
  quotation_id: number;
  proposal_id: number;
  proposal_status: string;
  disbursement_target_date: string;
}

export interface AssinaturaResumoResponse {
  status: string;
  mova: AssinaturaResumoMova;
  financial: AssinaturaResumoFinancial;
  signature: AssinaturaResumoSignature;
  next_actions: AssinaturaResumoNextActions;
}

export async function buscarResumoAssinatura(
  operacao_id: string,
): Promise<AssinaturaResumoResponse> {
  const {data} = await api.get<AssinaturaResumoResponse>(
    '/credito/mova/assinatura/resumo',
    {params: {operacao_id}},
  );
  return data;
}

export type TokenMethod = 'sms' | 'tts' | 'email';

// Step 1: Solicitar token OTP (SMS/TTS/Email)
export interface SolicitarTokenRequest {
  operacao_id: string;
  token_method?: TokenMethod[];
}

export interface SolicitarTokenResponse {
  ok: boolean;
  operacao_id: string;
  mova?: any;
}

export async function solicitarTokenAssinatura(
  payload: SolicitarTokenRequest,
): Promise<SolicitarTokenResponse> {
  const {data} = await api.post<SolicitarTokenResponse>(
    '/credito/mova/assinatura/token',
    payload,
  );
  return data;
}

// Step 2: Confirmar assinatura com token
export interface ConfirmarAssinaturaRequest {
  operacao_id: string;
  token_number: string;
  terms_content: string;
  // Dados opcionais de auditoria
  city?: string;
  state?: string;
  country?: string;
  geolocation?: string;
  device_manufacturer?: string;
  device_model?: string;
  device_os?: string;
  device_os_version?: string;
}

export interface ConfirmarAssinaturaResponse {
  ok: boolean;
  [key: string]: any;
}

export async function confirmarAssinatura(
  payload: ConfirmarAssinaturaRequest,
): Promise<ConfirmarAssinaturaResponse> {
  const {data} = await api.post<ConfirmarAssinaturaResponse>(
    '/credito/mova/assinatura',
    payload,
  );
  return data;
}

// --- Listar Propostas ---

export type PropostaNextAction =
  | 'continuar'
  | 'aguardando_analise'
  | 'acompanhar'
  | 'assinar'
  | 'reprovada'
  | 'cancelada';

export interface PropostaItemAPI {
  operacao_id: string;
  status: string;
  tipo_produto: string;
  valor_solicitado_centavos: number;
  valor_aprovado_centavos: number;
  valor_contratado_centavos: number;
  prazo_meses: number;
  mova_proposal_id: number;
  mova_proposal_status: string;
  created_at: string;
  updated_at: string;
  next_action: PropostaNextAction;
}

export interface ListarPropostasResponse {
  items: PropostaItemAPI[];
}

export async function listarPropostas(): Promise<ListarPropostasResponse> {
  const {data} = await api.get<ListarPropostasResponse>(
    '/credito/mova/propostas',
  );
  return data;
}

// --- Listar Empréstimos ---

export type EmprestimoNextAction = 'acompanhar_emprestimo' | 'visualizar';

export interface EmprestimoItemAPI {
  operacao_id: string;
  status: string;
  tipo_produto: string;
  valor_contratado_centavos: number;
  valor_desembolsado_centavos: number;
  prazo_meses: number;
  taxa_juros_mensal: number;
  data_aprovacao: string;
  data_assinatura: string;
  data_desembolso: string;
  data_quitacao: string | null;
  saldo_total_centavos: number;
  encargos_total_centavos: number;
  mova_proposal_id: number;
  mova_proposal_status: string;
  next_action: EmprestimoNextAction;
}

export interface ListarEmprestimosResponse {
  items: EmprestimoItemAPI[];
}

export async function listarEmprestimos(): Promise<ListarEmprestimosResponse> {
  const {data} = await api.get<ListarEmprestimosResponse>(
    '/credito/mova/emprestimos',
  );
  return data;
}

export type ParcelaStatusVisual = 'a_vencer' | 'vence_hoje' | 'em_atraso' | 'paga';

export interface ParcelaItemAPI {
  id: string;
  numero_parcela: number;
  data_vencimento: string;
  status: string;
  status_visual: ParcelaStatusVisual;
  dias_atraso: number;
  valor_parcela_centavos: number;
  saldo_centavos: number;
  multa_atraso_centavos: number;
  mora_atraso_centavos: number;
  encargos_atraso_centavos: number;
  valor_atualizado_centavos: number;
  pode_pagar: boolean;
}

export interface ListarParcelasResponse {
  operacao_id: string;
  status_operacao: string;
  saldo_total_operacao_centavos: number;
  saldo_total_parcelas_centavos: number;
  valor_total_atualizado_centavos: number;
  parcelas_disponiveis: boolean;
  total_parcelas: number;
  items: ParcelaItemAPI[];
}

export async function listarParcelasEmprestimo(
  operacaoId: string,
): Promise<ListarParcelasResponse> {
  const {data} = await api.get<ListarParcelasResponse>(
    `/credito/mova/emprestimos/${operacaoId}/parcelas`,
  );
  return data;
}

// --- Elegibilidade ---

export interface ElegibilidadeResponse {
  elegivel: boolean;
}

export async function verificarElegibilidade(): Promise<ElegibilidadeResponse> {
  const {data} = await api.get<ElegibilidadeResponse>(
    '/credito/mova/elegibilidade',
  );
  return data;
}
